// Écran « Ma gamme » : liste des produits (maison / distribués), filtres (nature, famille, protocole, alertes, recherche),
// alertes (fin d'accord, documents de conformité), recherche d'un numéro de série. Option activée par l'éditeur.
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Boxes, Plus, Search } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { date, euros } from '../../lib/format.ts';
import { useModules } from '../../lib/modules.ts';
import { useSession } from '../../lib/session.tsx';
import { Alerte, Badge, Bouton, Champ, ChampChoix, Chargement, EntetePage, EtatErreur, EtatVide, LienBouton, Panneau, useNotifier } from '../../components/ui.tsx';
import { LIBELLES_PROTOCOLES, PROTOCOLES } from '@shared/gamme.ts';
import { BadgeNature, Protocoles, texteAlerteAccord, texteDelai, type AlerteAccord, type AlerteConformite, type Produit, type ReglagesGamme } from './commun.tsx';

interface Liste {
  aujourdhui: string; droits: { ecrire: boolean; achats: boolean; tarif?: boolean }; reglages?: ReglagesGamme | null; familles: string[];
  produits: (Produit & { alerte_accord: AlerteAccord | null; alerte_conformite: AlerteConformite | null; alerte_stock?: boolean })[];
  alertes: { accords: AlerteAccord[]; conformite: AlerteConformite[]; stock?: { produit_id: string; designation: string; stock: string; unite: string }[] };
  nb: { total: number; maison: number; distribue: number };
}

export function PageGamme() {
  const actif = useModules().ma_gamme;
  const { peut } = useSession();
  const [params, setParams] = useSearchParams();
  const filtre = (k: string) => params.get(k) ?? '';
  const changer = (k: string, v: string) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  const [saisie, setSaisie] = useState(filtre('q'));
  const qs = new URLSearchParams();
  for (const k of ['nature', 'famille', 'protocole', 'alerte', 'q']) if (filtre(k)) qs.set(k, filtre(k));
  const q = useQuery({ queryKey: ['gamme', qs.toString()], enabled: actif === true, queryFn: () => api.get<Liste>(`/api/gamme?${qs}`) });
  if (actif === undefined) return <Chargement />;
  if (actif === false) return <Panneau><EtatVide titre="Fonction non activée" texte="Cette fonction n’est pas activée pour votre société." /></Panneau>;
  const ecrire = peut('gamme.ecrire');
  const d = q.data;
  return (
    <div className="pile-l mg-page">
      <EntetePage titre="Ma gamme" sousTitre="Vos produits à votre marque et les produits que vous distribuez : compatibilités, documents, accords, stock et numéros de série."
        actions={ecrire ? <LienBouton to="/gamme/nouveau" variante="principal" icone={<Plus aria-hidden />}>Nouveau produit</LienBouton> : undefined} />
      {d && (d.alertes.accords.length > 0 || d.alertes.conformite.length > 0 || (d.alertes.stock?.length ?? 0) > 0) && (
        <Alerte ton="ambre" titre="À surveiller">
          <ul className="mg-alertes">
            {d.alertes.accords.map((a) => <li key={`a-${a.produit_id}`}><Link to={`/gamme/${a.produit_id}?onglet=accords`}>{a.designation}</Link> — {texteAlerteAccord(a, date)}</li>)}
            {(d.alertes.stock ?? []).map((s) => <li key={`s-${s.produit_id}`}><Link to={`/gamme/${s.produit_id}?onglet=series`}>{s.designation}</Link> — stock insuffisant ({Number(s.stock).toLocaleString('fr-FR')} {s.unite}), à régulariser par un inventaire</li>)}
            {d.alertes.conformite.length > 0 && (
              <li>{`${d.alertes.conformite.length} produit${d.alertes.conformite.length > 1 ? 's' : ''} dont les documents de conformité sont à compléter ou à renouveler`}
                {filtre('alerte') !== '1' && <> — <button type="button" className="mg-lien" onClick={() => changer('alerte', '1')}>les afficher</button></>}</li>
            )}
          </ul>
        </Alerte>
      )}
      <Panneau>
        <form className="mg-filtres" role="search" onSubmit={(e) => { e.preventDefault(); changer('q', saisie.trim()); }}>
          <div className="segment mg-segment" role="group" aria-label="Nature des produits">
            {[['', 'Tous'], ['maison', 'Maison'], ['distribue', 'Distribués']].map(([v, l]) => (
              <button key={v} type="button" aria-pressed={filtre('nature') === v} onClick={() => changer('nature', v)}>{l}</button>
            ))}
          </div>
          <ChampChoix libelle="Famille" vide="Toutes" value={filtre('famille')} onChange={(e) => changer('famille', e.target.value)}
            options={(d?.familles ?? []).map((f) => ({ valeur: f, libelle: f }))} />
          <ChampChoix libelle="Protocole" vide="Tous" value={filtre('protocole')} onChange={(e) => changer('protocole', e.target.value)}
            options={PROTOCOLES.filter((p) => p !== 'autre').map((p) => ({ valeur: p, libelle: LIBELLES_PROTOCOLES[p] }))} />
          <div className="mg-recherche">
            <Champ libelle="Rechercher" value={saisie} onChange={(e) => setSaisie(e.target.value)} placeholder="Désignation, référence, marque…" />
            <Bouton type="submit" icone={<Search aria-hidden />} aria-label="Lancer la recherche">Chercher</Bouton>
          </div>
          <label className="case mg-case"><input type="checkbox" checked={filtre('alerte') === '1'} onChange={(e) => changer('alerte', e.target.checked ? '1' : '')} /> En alerte seulement</label>
        </form>
      </Panneau>
      {q.isLoading && <Chargement />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {d && d.produits.length === 0 && (
        <Panneau><EtatVide icone={<Boxes />} titre={d.nb.total ? 'Aucun produit ne correspond' : 'Votre gamme est vide'}
          texte={d.nb.total ? 'Modifiez les filtres ou la recherche.' : 'Ajoutez vos produits à votre marque et les produits que vous distribuez ; ils seront proposés en premier dans vos devis.'}
          action={ecrire && !d.nb.total ? <LienBouton to="/gamme/nouveau" variante="principal">Ajouter un produit</LienBouton> : undefined} /></Panneau>
      )}
      {d && d.produits.length > 0 && (
        <Panneau serre titre={`${d.produits.length} produit${d.produits.length > 1 ? 's' : ''}`}>
          <ul className="liste mg-liste">
            {d.produits.map((p) => (
              <li key={p.id} className="liste-element mg-element">
                <div className="liste-element-corps">
                  <Link to={`/gamme/${p.id}`} className="liste-element-titre mg-titre">{p.designation}</Link>
                  <span className="rangee mg-meta">
                    <span className="mono petit">{p.reference}</span><BadgeNature nature={p.nature} />
                    {p.marque && <span className="petit secondaire">{p.marque}</span>}
                    {p.demo && <Badge ton="ambre">Démo</Badge>}
                    {!p.actif && <Badge>Désactivé</Badge>}
                  </span>
                  <Protocoles liste={p.protocoles} />
                  {p.alerte_accord && <Badge ton={p.alerte_accord.etat === 'echu' ? 'danger' : 'ambre'}>{texteAlerteAccord(p.alerte_accord, date)}</Badge>}
                  {p.alerte_conformite && (p.alerte_conformite.manquants.length > 0 || p.alerte_conformite.expires.length > 0) && <Badge ton="ambre">Documents à compléter</Badge>}
                  {p.alerte_stock && <Badge ton="ambre">Stock insuffisant</Badge>}
                </div>
                <div className="mg-chiffres petit">
                  <span>Stock : <strong className="chiffre">{Number(p.stock).toLocaleString('fr-FR')}</strong> {p.unite}</span>
                  {p.achats?.prix_effectif && <span className="secondaire">Achat : {p.achats.prix_effectif.prix === null ? 'à renseigner' : euros(p.achats.prix_effectif.prix)}</span>}
                </div>
              </li>
            ))}
          </ul>
        </Panneau>
      )}
      <RechercheSerie />
      {d?.reglages && <ReglageDelaiAccord r={d.reglages} />}
    </div>
  );
}

/**
 * Délai d'alerte de fin d'accord (réglage de la société, 31/10/2026) : visible des rôles qui voient les accords ; modifiable par
 * l'administrateur (comme les autres réglages de la société). Le délai sert partout : liste, fiche produit, alerte de l'accueil.
 */
function ReglageDelaiAccord({ r }: { r: ReglagesGamme }) {
  const qc = useQueryClient(); const notifier = useNotifier();
  const [saisie, setSaisie] = useState(String(r.alerte_accord_jours));
  useEffect(() => setSaisie(String(r.alerte_accord_jours)), [r.alerte_accord_jours]);
  const n = Number(saisie);
  const valide = /^\d{1,3}$/.test(saisie.trim()) && n >= r.min && n <= r.max;
  const enregistrer = useMutation({
    mutationFn: () => api.put<ReglagesGamme>('/api/gamme/reglages', { alerteAccordJours: n }),
    onSuccess: async (x) => { await qc.invalidateQueries({ queryKey: ['gamme'] }); await qc.invalidateQueries({ queryKey: ['accueil'] }); notifier(`Délai d’alerte réglé à ${texteDelai(x.alerte_accord_jours)}.`); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <Panneau titre="Alerte de fin d’accord">
      <p className="petit secondaire">
        Un accord de distribution est signalé « fin proche » (ici et sur l’accueil) quand il finit dans les {texteDelai(r.alerte_accord_jours)},
        sans accord suivant qui prend le relais. {r.alerte_accord_jours === r.defaut ? 'Valeur par défaut.' : `Par défaut : ${texteDelai(r.defaut)}.`}
      </p>
      {r.regler ? (
        <form className="mg-recherche" onSubmit={(e) => { e.preventDefault(); if (valide) enregistrer.mutate(); }}>
          <Champ libelle="Délai d’alerte (jours)" type="number" inputMode="numeric" min={r.min} max={r.max} value={saisie} onChange={(e) => setSaisie(e.target.value)}
            aide={`De ${r.min} à ${r.max} jours. Réglage de la société, pour tous les comptes.`} erreur={saisie && !valide ? `Nombre entier de ${r.min} à ${r.max}.` : undefined} />
          <Bouton type="submit" icone={<BellRing aria-hidden />} chargement={enregistrer.isPending} disabled={!valide || n === r.alerte_accord_jours}>Enregistrer le délai</Bouton>
        </form>
      ) : <p className="petit">Réglé par l’administrateur de votre société.</p>}
    </Panneau>
  );
}

interface Serie { id: string; numero: string; statut: string; date_pose: string | null; produit: { id: string; designation: string; reference: string };
  site_nom: string | null; client_nom: string | null; chantier_reference: string | null; site_masque?: boolean }
export const STATUTS_SERIE: Record<string, string> = { en_stock: 'En stock', pose: 'Posé', retourne: 'Retourné', hors_service: 'Hors service' };

function RechercheSerie() {
  const [saisie, setSaisie] = useState('');
  const [numero, setNumero] = useState('');
  const r = useQuery({ queryKey: ['gamme-serie', numero], enabled: !!numero, queryFn: () => api.get<{ elements: Serie[] }>(`/api/gamme/series?numero=${encodeURIComponent(numero)}`) });
  return (
    <Panneau titre="Retrouver un numéro de série">
      <form className="mg-recherche" onSubmit={(e) => { e.preventDefault(); setNumero(saisie.trim()); }}>
        <Champ libelle="Numéro de série" mono value={saisie} onChange={(e) => setSaisie(e.target.value)} />
        <Bouton type="submit" icone={<Search aria-hidden />} disabled={!saisie.trim()}>Retrouver</Bouton>
      </form>
      {r.isLoading && numero && <Chargement lignes={1} />}
      {r.data && r.data.elements.length === 0 && <p className="petit secondaire">Aucun produit de votre gamme ne porte le numéro « {numero} ».</p>}
      {r.data && r.data.elements.length > 0 && (
        <ul className="mg-series-trouvees">
          {r.data.elements.map((s) => (
            <li key={s.id} className="petit">
              <strong className="mono">{s.numero}</strong> — <Link to={`/gamme/${s.produit.id}?onglet=series`}>{s.produit.designation}</Link> ({STATUTS_SERIE[s.statut] ?? s.statut})
              {s.site_masque ? ' — site non autorisé pour votre compte' : [s.client_nom, s.site_nom, s.chantier_reference, s.date_pose ? `posé le ${date(s.date_pose)}` : null].filter(Boolean).map((x) => ` — ${x}`).join('')}
            </li>
          ))}
        </ul>
      )}
    </Panneau>
  );
}
